import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './ApiConfig';

const PromoVideoSlider = () => {
  const [videos, setVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentVideoUrl, setCurrentVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const videoRef = useRef(null); // Reference to the video element
  const [wasPlaying, setWasPlaying] = useState(false); // Track if video was playing before pause

  // Fetch video metadata
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/promo-videos`)
      .then(response => {
        if (isMounted) {
          console.log('Fetched videos:', response.data);
          if (Array.isArray(response.data) && response.data.every(v => v.id && typeof v.id === 'number')) {
            setVideos(response.data.map(video => ({
              ...video,
              isActive: video.isActive ?? video.active ?? false
            })));
            setLoading(false);
          } else {
            setError('Invalid video metadata received');
            setLoading(false);
          }
        }
      })
      .catch(error => {
        if (isMounted) {
          console.error('Error fetching videos:', error);
          setError('Failed to load video metadata');
          setLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch video blob when currentIndex changes
  useEffect(() => {
    let isMounted = true;
    if (currentVideoUrl) {
      URL.revokeObjectURL(currentVideoUrl);
      setCurrentVideoUrl(null);
    }

    if (videos.length > 0 && videos[currentIndex] && videos[currentIndex].id) {
      setLoading(true);
      const videoId = videos[currentIndex].id;
      axios
        .get(`${API_BASE_URL}/api/promo-videos/${videoId}/video`, {
          responseType: 'blob',
        })
        .then(res => {
          if (isMounted) {
            if (res.data instanceof Blob && res.data.type.startsWith('video/')) {
              const fileUrl = URL.createObjectURL(res.data);
              console.log('Video URL created for ID', videoId, ':', fileUrl);
              setCurrentVideoUrl(fileUrl);
              setLoading(false);
              setWasPlaying(true); // Assume video will play on load due to autoPlay
            } else {
              console.error('Invalid video blob received:', res.data);
              setError('Invalid video data received');
              setLoading(false);
            }
          }
        })
        .catch(err => {
          if (isMounted) {
            console.error('Error loading video blob for ID', videoId, ':', err);
            setError('Failed to load video');
            setLoading(false);
          }
        });
    }
    return () => {
      isMounted = false;
      if (currentVideoUrl) {
        URL.revokeObjectURL(currentVideoUrl);
      }
    };
  }, [currentIndex, videos]);

  // Intersection Observer to pause/resume based on visibility
  useEffect(() => {
    if (!videoRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (videoRef.current) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.1) {
            // Video is not visible (or less than 10% visible)
            if (!videoRef.current.paused) {
              setWasPlaying(true);
              videoRef.current.pause();
              console.log('Video paused due to not being visible');
            }
          } else {
            // Video is visible
            if (wasPlaying && videoRef.current.paused) {
              videoRef.current.play().catch(err => {
                console.error('Error resuming video:', err);
                setError('Failed to resume video');
              });
              console.log('Video resumed due to being visible');
            }
          }
        }
      },
      { threshold: 0.1 } // Trigger when at least 10% of the video is visible
    );

    observer.observe(videoRef.current);

    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, [currentVideoUrl]); // Re-run when video URL changes

  const nextVideo = () => {
    setCurrentIndex(prevIndex => (prevIndex + 1) % videos.length);
  };

  const prevVideo = () => {
    setCurrentIndex(prevIndex => (prevIndex - 1 + videos.length) % videos.length);
  };

  if (error) {
    return <div style={{ textAlign: 'center', padding: '20px', color: '#DC2626', fontFamily: 'Arial, sans-serif' }}>{error}</div>;
  }

  if (loading) {
    return (
      <div style={{ 
        maxWidth: '800px', 
        margin: '0 auto', 
        padding: '20px', 
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#FFF7ED',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1F2937', marginBottom: '16px' }}>
          Promotional Videos
        </h2>
        <div style={{ position: 'relative', paddingTop: '56.25%', backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden' }}>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60px',
              height: '60px',
              border: '6px solid #DC2626',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              boxShadow: '0 0 15px rgba(220, 38, 38, 0.5)',
            }}
          />
          <style>
            {`
              @keyframes spin {
                0% { transform: translate(-50%, -50%) rotate(0deg); }
                100% { transform: translate(-50%, -50%) rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  if (videos.length === 0 || !videos[currentIndex] || !videos[currentIndex].id) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '20px', 
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#FFF7ED',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        No promotional videos available
      </div>
    );
  }

  const currentVideo = videos[currentIndex];

  return (
    <div style={{ 
      maxWidth: '800px', 
      margin: '0 auto', 
      padding: '20px', 
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#FFF7ED',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    }}>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1F2937', marginBottom: '16px' }}>
        Promotional Videos
      </h2>
      <div style={{ position: 'relative', paddingTop: '56.25%', backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden' }}>
        <video
          ref={videoRef}
          src={currentVideoUrl}
          controls
          autoPlay
          muted
          width="100%"
          height="100%"
          style={{ position: 'absolute', top: 0, left: 0 }}
          onError={() => setError('Failed to load video. Please try again.')}
          onEnded={nextVideo}
        />
        <button
          onClick={prevVideo}
          style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: videos.length <= 1 ? '#FCA5A5' : '#DC2626',
            color: '#FFFFFF',
            width: '3rem',
            height: '3rem',
            fontSize: '1.25rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            zIndex: 10,
            cursor: videos.length <= 1 ? 'not-allowed' : 'pointer',
            opacity: videos.length <= 1 ? 0.6 : 1,
          }}
          onMouseOver={e => e.currentTarget.style.backgroundColor = videos.length <= 1 ? '#FCA5A5' : '#B91C1C'}
          onMouseOut={e => e.currentTarget.style.backgroundColor = videos.length <= 1 ? '#FCA5A5' : '#DC2626'}
          disabled={videos.length <= 1}
        >
          ←
        </button>
        <button
          onClick={nextVideo}
          style={{
            position: 'absolute',
            right: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: videos.length <= 1 ? '#FCA5A5' : '#DC2626',
            color: '#FFFFFF',
            width: '3rem',
            height: '3rem',
            fontSize: '1.25rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            zIndex: 10,
            cursor: videos.length <= 1 ? 'not-allowed' : 'pointer',
            opacity: videos.length <= 1 ? 0.6 : 1,
          }}
          onMouseOver={e => e.currentTarget.style.backgroundColor = videos.length <= 1 ? '#FCA5A5' : '#B91C1C'}
          onMouseOut={e => e.currentTarget.style.backgroundColor = videos.length <= 1 ? '#FCA5A5' : '#DC2626'}
          disabled={videos.length <= 1}
        >
          →
        </button>
      </div>
      {/*
        <p><strong>Title:</strong> {currentVideo.title || 'No title'}</p>
        <p><strong>Description:</strong> {currentVideo.description || 'No description'}</p>
        <p><strong>Status:</strong> {currentVideo.isActive ? 'Active' : 'Inactive'}</p>
      */}
    </div>
  );
};

export default PromoVideoSlider;